<template>
  <div class="user-profile">
    <div class="container-fluid">
      <!-- Header de bienvenida -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="welcome-header">
            <div class="welcome-content">
              <h2 class="welcome-title">
                <i class="nc-icon nc-single-02"></i>
                Bienvenido, {{ user.name }}
              </h2>
              <p class="welcome-subtitle">
                {{ user.profile }} - {{ user.system.name }}
              </p>
              <div class="welcome-badges">
                <span class="badge badge-primary">
                  <i class="nc-icon nc-globe-2"></i>
                  {{ user.company.country }}
                </span>
                <span class="badge" :class="getStatusBadgeClass(user.status)">
                  <i class="nc-icon nc-check-2"></i>
                  {{ user.status }}
                </span>
                <span class="badge badge-info">
                  <i class="nc-icon nc-world-2"></i>
                  {{ user.language }}
                </span>
                <span v-if="user.globalAccess" class="badge badge-warning">
                  <i class="nc-icon nc-key-25"></i>
                  Acceso Global
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <!-- Columna Izquierda: Perfil e Información -->
        <div class="col-xl-4 col-lg-5 col-md-6">
          <!-- Tarjeta de Perfil Principal -->
          <div class="card card-profile">
            <div class="card-header card-header-primary text-center">
              <div class="card-avatar">
                <img
                  :src="user.avatar || '/static/img/central-gym150x150.webp'"
                  alt="Avatar"
                  class="img-fluid rounded-circle"
                />
              </div>
            </div>
            <div class="card-body">
              <h4 class="card-title text-center">{{ user.name }}</h4>
              <h6 class="card-category text-center text-muted">
                @{{ user.username }}
              </h6>
              <div class="profile-stats mt-4">
                <div class="row">
                  <div class="col-4">
                    <div class="stat-item text-center">
                      <h3 class="stat-number text-primary">{{ user.branches.total }}</h3>
                      <p class="stat-label">Sucursales</p>
                    </div>
                  </div>
                  <div class="col-4">
                    <div class="stat-item text-center">
                      <h3 class="stat-number text-success">{{ user.branches.physical }}</h3>
                      <p class="stat-label">Físicas</p>
                    </div>
                  </div>
                  <div class="col-4">
                    <div class="stat-item text-center">
                      <h3 class="stat-number text-info">{{ user.branches.virtual }}</h3>
                      <p class="stat-label">Virtuales</p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="profile-actions mt-4">
                <button
                  class="btn btn-primary btn-round btn-block"
                  @click="handlePasswordChange"
                >
                  <i class="nc-icon nc-key-25"></i>
                  Cambiar Contraseña
                </button>
              </div>
            </div>
          </div>

          <!-- Información del Sistema y Empresa -->
          <div class="card mt-4">
            <div class="card-header">
              <h5 class="card-title">
                <i class="nc-icon nc-building"></i>
                Información Corporativa
              </h5>
            </div>
            <div class="card-body">
              <div class="info-section">
                <h6 class="info-section-title">
                  <i class="nc-icon nc-app"></i>
                  Sistema
                </h6>
                <div class="info-item">
                  <strong>Aplicación:</strong>
                  <span>{{ user.system.name }}</span>
                </div>
                <div class="info-item">
                  <strong>ID Sistema:</strong>
                  <span class="text-muted small">{{ user.system.id }}</span>
                </div>
              </div>

              <div class="info-section">
                <h6 class="info-section-title">
                  <i class="nc-icon nc-shop"></i>
                  Empresa
                </h6>
                <div class="info-item">
                  <strong>Nombre:</strong>
                  <span>{{ user.company.name }}</span>
                </div>
                <div class="info-item">
                  <strong>ID Empresa:</strong>
                  <span class="text-muted small">{{ user.company.id }}</span>
                </div>
                <div class="info-item">
                  <strong>País:</strong>
                  <span>{{ user.company.country }}</span>
                </div>
                <div class="info-item">
                  <strong>Zona Horaria:</strong>
                  <span>{{ user.company.timezone }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Acceso y Configuración -->
          <div class="card mt-4">
            <div class="card-header">
              <h5 class="card-title">
                <i class="nc-icon nc-settings-gear-65"></i>
                Configuración de Acceso
              </h5>
            </div>
            <div class="card-body">
              <div class="access-item">
                <div class="access-icon">
                  <i class="nc-icon nc-world-2" :class="user.globalAccess ? 'text-success' : 'text-muted'"></i>
                </div>
                <div class="access-content">
                  <strong>Acceso Global</strong>
                  <span :class="user.globalAccess ? 'text-success' : 'text-muted'">
                    {{ user.globalAccess ? 'Habilitado' : 'Deshabilitado' }}
                  </span>
                </div>
              </div>
              <div class="access-item">
                <div class="access-icon">
                  <i class="nc-icon nc-globe-2 text-info"></i>
                </div>
                <div class="access-content">
                  <strong>Idioma</strong>
                  <span>{{ user.language }}</span>
                </div>
              </div>
              <div class="access-item">
                <div class="access-icon">
                  <i class="nc-icon nc-refresh-69" :class="user.passwordUpdate ? 'text-warning' : 'text-success'"></i>
                </div>
                <div class="access-content">
                  <strong>Contraseña</strong>
                  <span :class="user.passwordUpdate ? 'text-warning' : 'text-success'">
                    {{ user.passwordUpdate ? 'Requiere actualización' : 'Actualizada' }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Columna Central: Sucursales -->
        <div class="col-xl-4 col-lg-7 col-md-6">
          <div class="card h-100">
            <div class="card-header">
              <h5 class="card-title">
                <i class="nc-icon nc-pin-3"></i>
                Sucursales Asignadas ({{ user.branches.total }})
              </h5>
              <div class="card-category">
                Gestiona tus sucursales y configuraciones
              </div>
            </div>
            <div class="card-body">
              <div class="branches-container">
                <div
                  v-for="branch in user.branchList"
                  :key="branch._id"
                  class="branch-card"
                  :class="{ 
                    'active': branch._id === user.currentBranch,
                    'prioritized': branch.isPrioritized,
                    'physical': branch.type === 'FISICA',
                    'virtual': branch.type === 'VIRTUAL'
                  }"
                >
                  <div class="branch-header">
                    <div class="branch-type">
                      <i :class="getBranchIcon(branch.type)"></i>
                      <span class="branch-type-text">{{ branch.type }}</span>
                    </div>
                    <div class="branch-badges">
                      <span v-if="branch._id === user.currentBranch" class="badge badge-success">
                        Actual
                      </span>
                      <span v-if="branch.isPrioritized" class="badge badge-warning">
                        Prioritaria
                      </span>
                    </div>
                  </div>
                  
                  <div class="branch-content">
                    <h6 class="branch-name">{{ branch.nombreSucursal }}</h6>
                    <div class="branch-id">
                      <small class="text-muted">ID: {{ branch._id }}</small>
                    </div>
                  </div>

                  <!-- Integraciones -->
                  <div class="branch-integrations" v-if="branch.integrations">
                    <div class="integration-item" v-if="branch.integrations.bsale">
                      <div class="integration-status" :class="branch.integrations.bsale.active ? 'active' : 'inactive'">
                        <i class="nc-icon nc-money-coins"></i>
                        <span>BSale</span>
                        <i class="nc-icon" :class="branch.integrations.bsale.active ? 'nc-check-2' : 'nc-simple-remove'"></i>
                      </div>
                    </div>
                    <div class="integration-item" v-if="branch.integrations.flow">
                      <div class="integration-status" :class="branch.integrations.flow.active ? 'active' : 'inactive'">
                        <i class="nc-icon nc-credit-card"></i>
                        <span>Flow</span>
                        <i class="nc-icon" :class="branch.integrations.flow.active ? 'nc-check-2' : 'nc-simple-remove'"></i>
                      </div>
                    </div>
                  </div>

                  <!-- Configuración de BD (solo para sucursales virtuales con configuración) -->
                  <div class="branch-config" v-if="branch.dbConfig">
                    <div class="config-indicator">
                      <i class="nc-icon nc-database text-info"></i>
                      <small>BD Configurada</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Columna Derecha: Formulario de Edición -->
        <div class="col-xl-4 col-lg-12">
          <div class="card">
            <div class="card-header card-header-primary">
              <h4 class="card-title">
                <i class="nc-icon nc-settings-gear-65"></i>
                Configuración del Perfil
              </h4>
              <p class="card-category">Actualiza tu información personal</p>
            </div>
            <div class="card-body">
              <form @submit.prevent="updateProfile">
                <!-- Información Personal -->
                <h5 class="section-title">
                  <i class="nc-icon nc-circle-10"></i>
                  Información Personal
                </h5>
                <div class="row">
                  <div class="col-md-12">
                    <fg-input
                      type="text"
                      label="Nombre Completo"
                      v-model="user.name"
                      :disabled="!editMode"
                    />
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-6">
                    <fg-input
                      type="text"
                      label="Usuario"
                      v-model="user.username"
                      :disabled="!editMode"
                    />
                  </div>
                  <div class="col-md-6">
                    <fg-input
                      type="email"
                      label="Email"
                      v-model="user.email"
                      :disabled="!editMode"
                    />
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-6">
                    <fg-input
                      type="text"
                      label="RUT"
                      v-model="user.rut"
                      :disabled="!editMode"
                      placeholder="Sin información"
                    />
                  </div>
                  <div class="col-md-6">
                    <fg-input
                      type="text"
                      label="Teléfono"
                      v-model="user.phone"
                      :disabled="!editMode"
                      placeholder="Sin información"
                    />
                  </div>
                </div>

                <!-- Información de Sesión -->
                <h5 class="section-title mt-4">
                  <i class="nc-icon nc-time-alarm"></i>
                  Información de Sesión
                </h5>
                <div class="row">
                  <div class="col-md-6">
                    <fg-input
                      type="text"
                      label="Último Acceso"
                      v-model="user.lastLogin"
                      disabled
                    />
                  </div>
                  <div class="col-md-6">
                    <fg-input
                      type="text"
                      label="ID Usuario"
                      v-model="user.userId"
                      disabled
                    />
                  </div>
                </div>

                <!-- Información de Cuenta -->
                <h5 class="section-title mt-4">
                  <i class="nc-icon nc-badge"></i>
                  Información de Cuenta
                </h5>
                <div class="row">
                  <div class="col-md-6">
                    <fg-input
                      type="text"
                      label="Perfil"
                      v-model="user.profile"
                      disabled
                    />
                  </div>
                  <div class="col-md-6">
                    <fg-input
                      type="text"
                      label="Estado"
                      v-model="user.status"
                      disabled
                    />
                  </div>
                </div>

                <!-- Botones de Acción -->
                <div class="card-footer text-center">
                  <div class="row">
                    <div class="col-12">
                      <button
                        v-if="!editMode"
                        type="button"
                        class="btn btn-primary btn-round"
                        @click="enableEdit"
                      >
                        <i class="nc-icon nc-settings-gear-65"></i>
                        Editar Perfil
                      </button>
                      <div v-else class="edit-actions">
                        <button
                          type="submit"
                          class="btn btn-success btn-round"
                          :disabled="loading"
                        >
                          <i class="nc-icon nc-check-2"></i>
                          {{ loading ? 'Guardando...' : 'Guardar Cambios' }}
                        </button>
                        <button
                          type="button"
                          class="btn btn-secondary btn-round ml-2"
                          @click="cancelEdit"
                        >
                          <i class="nc-icon nc-simple-remove"></i>
                          Cancelar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Cambio de Contraseña -->
    <modal :show.sync="changingPassword" class="modal-primary">
      <h4 slot="header" class="modal-title">
        <i class="nc-icon nc-key-25"></i>
        Cambiar Contraseña
      </h4>
      <form @submit.prevent="updatePassword">
        <fg-input
          type="password"
          label="Contraseña Actual"
          v-model="passwordChangeForm.old"
          placeholder="Ingresa tu contraseña actual"
        />
        <fg-input
          type="password"
          label="Nueva Contraseña"
          v-model="passwordChangeForm.new"
          placeholder="Ingresa tu nueva contraseña"
        />
        <fg-input
          type="password"
          label="Confirmar Contraseña"
          v-model="passwordChangeForm.confirm"
          placeholder="Confirma tu nueva contraseña"
        />
        <small v-if="passwordUpdateErrorMessage" class="text-danger">
          {{ passwordUpdateErrorMessage }}
        </small>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            @click="changingPassword = false"
          >
            Cancelar
          </button>
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="
              !passwordChangeForm.old ||
              !passwordChangeForm.new ||
              passwordChangeForm.new !== passwordChangeForm.confirm
            "
          >
            <i class="nc-icon nc-check-2"></i>
            Cambiar Contraseña
          </button>
        </div>
      </form>
    </modal>
  </div>
</template>

<script>
import { Modal } from "src/components/UIComponents";

export default {
  name: "UserProfile",
  components: {
    Modal,
  },
  data() {
    return {
      editMode: false,
      loading: false,
      originalUser: {},
      changingPassword: false,
      passwordChangeForm: {
        old: "",
        new: "",
        confirm: "",
      },
      user: {
        // Información básica del usuario
        userId: "",
        name: "",
        username: "",
        email: "",
        rut: "",
        phone: "",
        lastLogin: "",
        profile: "",
        status: "",
        avatar: "",
        globalAccess: false,
        language: "",
        passwordUpdate: false,

        // Información del sistema
        system: {
          id: "",
          name: "",
        },

        // Información de la empresa
        company: {
          id: "",
          name: "",
          country: "",
          timezone: "",
          clientId: "",
        },

        // Información de sucursales
        branches: {
          total: 0,
          physical: 0,
          virtual: 0,
        },
        branchList: [],
        currentBranch: "",
      },
    };
  },
  computed: {
    passwordUpdateErrorMessage() {
      if (
        this.passwordChangeForm.new &&
        this.passwordChangeForm.new !== this.passwordChangeForm.confirm
      ) {
        return "Las contraseñas deben coincidir";
      }
      return null;
    },
  },
  methods: {
    loadUserData() {
      const userData = this.$store.getters.getUserData;
      
      // Debug: verificar si hay datos en el store
      console.log("Datos del store:", userData);
      
      if (!userData || Object.keys(userData).length === 0) {
        console.warn("No hay datos de usuario en el store, intentando obtener desde sessionStorage");
        
        // Fallback: intentar obtener algunos datos básicos desde sessionStorage
        const savedBranches = JSON.parse(sessionStorage.getItem("xxxwypt5ip") || "[]");
        
        this.user = {
          userId: "No disponible",
          name: sessionStorage.getItem("WHbUjdv3TG") || "Usuario Demo",
          username: sessionStorage.getItem("WHbUjdv3TG") || "demo.user",
          email: sessionStorage.getItem("email") || "demo@centralgym.cl",
          rut: "12.345.678-9",
          phone: "+56 9 1234 5678",
          lastLogin: new Date().toLocaleString(),
          profile: sessionStorage.getItem("account-type") || "ADMINISTRADOR",
          status: "ACTIVA",
          avatar: null,
          globalAccess: true,
          language: "Español (Chile)",
          passwordUpdate: false,

          system: {
            id: "5a80fcd2a893142078bf78bf",
            name: "CentralGym App",
          },

          company: {
            id: sessionStorage.getItem("eId") || "5a8657c9dbad941cacd25458",
            name: "2Tec Sistemas Spa",
            country: "Chile",
            timezone: "America/Santiago",
            clientId: "5aa6d3b1639b8e0da844b480",
          },

          branches: {
            total: savedBranches.length || 3,
            physical: 1,
            virtual: 2,
          },
          branchList: savedBranches.length > 0 ? savedBranches : [
            {
              _id: "5a865898dbad941cacd2545b",
              nombreSucursal: "Servidor Azure - BD_MCONTROL_DESA",
              type: "VIRTUAL",
              isPrioritized: true,
              integrations: {
                bsale: { active: true },
                flow: { active: true }
              },
              dbConfig: true
            },
            {
              _id: "643ec22da105b1516d8d7526", 
              nombreSucursal: "Servidor Azure - BD_MCONTROL_V_12",
              type: "VIRTUAL",
              isPrioritized: false,
              integrations: {
                bsale: { active: false },
                flow: { active: false }
              },
              dbConfig: true
            },
            {
              _id: "65dbf7880b15c41ee4cc94e5",
              nombreSucursal: "OFICINA OCHAGAVIA", 
              type: "FISICA",
              isPrioritized: false,
              integrations: {
                bsale: { active: false },
                flow: { active: false }
              },
              dbConfig: false
            }
          ],
          currentBranch: sessionStorage.getItem("nu5za9cv6q") || "",
        };
        return;
      }

      // Procesar información de sucursales
      const branchList = userData.sucursales || [];
      const physicalBranches = branchList.filter(branch => 
        branch.tipoSucursal?.glosa === "FISICA"
      ).length;
      const virtualBranches = branchList.filter(branch => 
        branch.tipoSucursal?.glosa === "VIRTUAL"
      ).length;

      // Formatear sucursales con información completa
      const formattedBranches = branchList.map(branch => ({
        _id: branch._id,
        nombreSucursal: branch.nombreSucursal,
        type: branch.tipoSucursal?.glosa || "NO_DEFINIDO",
        isPrioritized: branch.isPrioritized || false,
        integrations: {
          bsale: {
            active: branch.integraciones?.bsale?.indicadorActivaBsale || false,
            url: branch.integraciones?.bsale?.urlBsaleAPI || "",
            token: branch.integraciones?.bsale?.tokenBSale || "",
          },
          flow: {
            active: branch.integraciones?.flow?.indicadorActivaFlow || false,
            url: branch.integraciones?.flow?.urlFlowAPI || "",
            apiKey: branch.integraciones?.flow?.apiKey || "",
          },
        },
        dbConfig: branch.configuracion?.BD ? true : false,
        tokenBSale: branch.tokenBSale || "",
      }));

      this.user = {
        // Información básica del usuario
        userId: userData.idUsuario || "No disponible",
        name: userData.nombreUsuario || "Usuario",
        username: userData.usuarioSesion || "Sin usuario",
        email: userData.email || "Sin email",
        rut: userData.informacionAdicional?.rut || "",
        phone: userData.informacionAdicional?.celular || "",
        lastLogin: userData.fechaUltimoAcceso 
          ? (this.$moment ? this.$moment(userData.fechaUltimoAcceso).format("DD-MM-YYYY HH:mm:ss") : new Date(userData.fechaUltimoAcceso).toLocaleString())
          : "No disponible",
        profile: userData.perfilCuenta?.glosa || "Sin perfil",
        status: userData.estadoCuenta?.glosa || "Sin estado",
        avatar: userData.avatar || null,
        globalAccess: userData.accesoGlobal || false,
        language: this.formatLanguage(userData.idioma) || "Español (Chile)",
        passwordUpdate: userData.actualizarPassword || false,

        // Información del sistema
        system: {
          id: userData.sistema?._id || "No disponible",
          name: userData.sistema?.glosa || "CentralGym App",
        },

        // Información de la empresa
        company: {
          id: userData.empresa?._id || sessionStorage.getItem("eId") || "No disponible",
          name: userData.empresa?.nombreFantasia || "Sin empresa",
          country: userData.empresa?.cliente?.nacionalidad || "Chile",
          timezone: userData.empresa?.cliente?.zonaHoraria || "America/Santiago",
          clientId: userData.empresa?.cliente?._id || "No disponible",
        },

        // Información de sucursales
        branches: {
          total: branchList.length,
          physical: physicalBranches,
          virtual: virtualBranches,
        },
        branchList: formattedBranches,
        currentBranch: sessionStorage.getItem("nu5za9cv6q") || "",
      };

      // Debug: verificar datos procesados
      console.log("Datos procesados del usuario:", this.user);
    },

    formatLanguage(lang) {
      const languages = {
        'es-CL': 'Español (Chile)',
        'es-ES': 'Español (España)',
        'en-US': 'English (US)',
        'en-GB': 'English (UK)',
        'pt-BR': 'Português (Brasil)',
      };
      return languages[lang] || lang || 'Español (Chile)';
    },

    getStatusColor(status) {
      switch (status?.toUpperCase()) {
        case "ACTIVA":
          return "text-success";
        case "BLOQUEADA":
        case "BLOQUEADA POR MORA":
          return "text-danger";
        case "PENDIENTE":
          return "text-warning";
        default:
          return "text-muted";
      }
    },

    getStatusBadgeClass(status) {
      switch (status?.toUpperCase()) {
        case "ACTIVA":
          return "badge-success";
        case "BLOQUEADA":
        case "BLOQUEADA POR MORA":
          return "badge-danger";
        case "PENDIENTE":
          return "badge-warning";
        default:
          return "badge-secondary";
      }
    },

    getBranchIcon(type) {
      switch (type?.toUpperCase()) {
        case "FISICA":
          return "nc-icon nc-shop text-success";
        case "VIRTUAL":
          return "nc-icon nc-cloud-upload-94 text-info";
        default:
          return "nc-icon nc-pin-3 text-muted";
      }
    },

    enableEdit() {
      this.editMode = true;
      this.originalUser = { ...this.user };
    },

    cancelEdit() {
      this.editMode = false;
      this.user = { ...this.originalUser };
    },

    async updateProfile() {
      if (!this.editMode) return;

      this.loading = true;
      this.$swal.fire({
        title: "Actualizando perfil...",
        text: "Por favor espera",
        showConfirmButton: false,
        allowOutsideClick: false,
        didOpen: () => {
          this.$swal.showLoading();
        },
      });

      try {
        // Simulamos la actualización del perfil
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        this.editMode = false;
        this.loading = false;
        
        this.$swal.fire({
          title: "¡Perfil actualizado!",
          text: "Tu información ha sido actualizada exitosamente",
          icon: "success",
          confirmButtonColor: "#6bd098",
        });
        
      } catch (error) {
        this.loading = false;
        this.$swal.fire({
          title: "Error",
          text: "No se pudo actualizar el perfil. Inténtalo nuevamente.",
          icon: "error",
          confirmButtonColor: "#6bd098",
        });
      }
    },

    handlePasswordChange() {
      this.changingPassword = true;
      this.passwordChangeForm = {
        old: "",
        new: "",
        confirm: "",
      };
    },

    async updatePassword() {
      this.$swal.fire({
        title: "Actualizando contraseña",
        showConfirmButton: false,
        allowOutsideClick: false,
        didOpen: () => {
          this.$swal.showLoading();
        },
      });

      try {
        const res = await this.$axiosLogin.post(
          this.$Operations.UpdatePassword,
          {
            Header: {
              Operacion: this.$Operations.UpdatePasswordOP,
            },
            Auth: {
              Token: sessionStorage.getItem("jCxbraoQeD"),
            },
            Body: {
              passwordActual: this.passwordChangeForm.old,
              nuevaPassword: this.passwordChangeForm.new,
              nuevaPasswordRepetir: this.passwordChangeForm.confirm,
            },
          },
          {
            timeout: 5000,
            headers: {
              "Content-Type": "application/json",
              token: sessionStorage.getItem("jCxbraoQeD"),
              operation: this.$Operations.UpdatePasswordOP,
            },
          }
        );

         const response = res.data.Response;
        if (response.Resultado.CodigoHTTP === 200 || response.Resultado.CodigoHTTP === 201) {
          this.passwordChangeForm = {
            old: "",
            new: "",
            confirm: "",
          };
          this.changingPassword = false;
          
          this.$swal.fire({
            title: "¡Éxito!",
            text: "Tu contraseña ha sido actualizada. Debes volver a iniciar sesión.",
            icon: "success",
            showConfirmButton: true,
            confirmButtonColor: "#6bd098",
          }).then(() => {
            sessionStorage.setItem("jCxbraoQeD", null);
            this.$router.push("/login");
          });
        } else {
          this.$swal.fire({
            title: "Error",
            text: response.Resultado.MensajeServicio || "Error al actualizar contraseña",
            icon: "error",
            confirmButtonColor: "#6bd098",
          });
        }
      } catch (error) {
        this.$swal.fire({
          title: "Error",
          text: error.response?.data?.Response?.Resultado?.MensajeServicio || "Error inesperado",
          icon: "error",
          confirmButtonColor: "#6bd098",
        });
      }
    },
  },
  
  created() {
    console.log("UserProfile component created");
  },

  beforeMount() {
    console.log("UserProfile component before mount");
    this.loadUserData();
  },
  
  mounted() {
    console.log("UserProfile component mounted");
    this.$swal.close();
    
    // Verificar datos después del montaje
    setTimeout(() => {
      console.log("Datos del usuario después del montaje:", this.user);
    }, 100);
  },
};
</script>

<style scoped>
  .user-profile {
    padding: 20px 0;
    min-height: 100vh;
    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  }

  /* Estilos generales de tarjetas */
  .card {
    border: none;
    border-radius: 18px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
    background: white;
  }

  .card:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  }

  .card-header {
    background: transparent !important;
    border-bottom: none !important;
    padding: 25px 25px 15px;
  }

  /* Excepción para el header primario del perfil */
  .card-header-primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
    color: white !important;
    border-radius: 20px 20px 0 0 !important;
    padding: 35px 20px 65px !important;
  }

  .card-header-primary .card-title {
    color: white !important;
  }

  .card-header-primary .card-category {
    color: rgba(255, 255, 255, 0.8) !important;
  }

  .card-title {
    font-weight: 500 !important;
    font-size: 1.3rem !important;
    color: #4a5568 !important;
    margin-bottom: 5px !important;
    letter-spacing: 0.3px;
  }

  .card-category {
    color: #718096 !important;
    font-size: 0.9rem !important;
    font-weight: 400 !important;
    margin: 0 !important;
  }

  .card-body {
    padding: 20px 25px 25px;
  }

  /* Espaciado específico para el card-body del perfil */
  .card-profile .card-body {
    padding: 65px 20px 20px;
  }

/* Header de Bienvenida */
.welcome-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 20px;
  padding: 35px;
  color: white;
  margin-bottom: 25px;
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
  position: relative;
  overflow: hidden;
}

.welcome-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 100%);
  pointer-events: none;
}

.welcome-content {
  position: relative;
  z-index: 1;
}

.welcome-title {
  font-size: 2.2rem;
  font-weight: 400;
  margin-bottom: 8px;
  letter-spacing: 0.5px;
}

.welcome-subtitle {
  font-size: 1.1rem;
  opacity: 0.85;
  margin-bottom: 20px;
  font-weight: 300;
}

.welcome-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.welcome-badges .badge {
  padding: 8px 15px;
  font-size: 0.9rem;
  font-weight: 500;
}

/* Tarjeta de Perfil */
.card-profile {
  border: none;
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.08);
  border-radius: 20px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.card-profile:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.12);
}

.card-header-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 20px 20px 0 0;
  padding: 35px 20px 65px;
  position: relative;
}

.card-avatar {
  position: absolute;
  bottom: -40px;
  left: 50%;
  transform: translateX(-50%);
  width: 90px;
  height: 90px;
  border-radius: 50%;
  overflow: hidden;
  border: 4px solid #fff;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  z-index: 10;
}

.card-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Estadísticas del Perfil */
.profile-stats {
  border-top: 1px solid #eee;
  border-bottom: 1px solid #eee;
  padding: 20px 0;
}

.stat-item {
  padding: 10px 0;
}

.stat-number {
  font-size: 1.8rem;
  font-weight: 600;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 0;
}

.profile-actions {
  padding: 20px 0;
}

/* Información Corporativa */
.info-section {
  margin-bottom: 25px;
  padding-bottom: 20px;
  border-bottom: 1px solid #f5f6fa;
}

.info-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.info-section-title {
  color: #5a67d8;
  font-weight: 500;
  margin-bottom: 15px;
  font-size: 1rem;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  opacity: 0.8;
}

.info-item {
  padding: 8px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.info-item strong {
  color: #333;
  flex-shrink: 0;
  margin-right: 15px;
}

.info-item span {
  text-align: right;
  word-break: break-word;
}

/* Configuración de Acceso */
.access-item {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.access-item:last-child {
  border-bottom: none;
}

.access-icon {
  width: 40px;
  text-align: center;
  margin-right: 15px;
}

.access-content {
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* Sucursales */
.branches-container {
  max-height: 600px;
  overflow-y: auto;
  padding-right: 10px;
}

  .branch-card {
    background: white;
    border-radius: 15px;
    padding: 18px;
    margin-bottom: 12px;
    border-left: 3px solid #e2e8f0;
    transition: all 0.3s ease;
    position: relative;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  }

  .branch-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
    border-left-color: #667eea;
  }

.branch-card.active {
  background: linear-gradient(135deg, #e8f5e8 0%, #f0f8f0 100%);
  border-left-color: #28a745;
  box-shadow: 0 4px 15px rgba(40, 167, 69, 0.2);
}

.branch-card.physical {
  border-left-color: #28a745;
}

.branch-card.virtual {
  border-left-color: #17a2b8;
}

.branch-card.prioritized {
  background: linear-gradient(135deg, #fff3cd 0%, #fef9e7 100%);
  border-left-color: #ffc107;
}

.branch-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.branch-type {
  display: flex;
  align-items: center;
  gap: 8px;
}

.branch-type-text {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.branch-badges {
  display: flex;
  gap: 5px;
}

.branch-content {
  margin-bottom: 15px;
}

.branch-name {
  font-weight: 600;
  color: #333;
  margin-bottom: 5px;
  line-height: 1.3;
}

.branch-id {
  margin-bottom: 10px;
}

/* Integraciones */
.branch-integrations {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}

.integration-item {
  flex: 1;
}

.integration-status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
}

.integration-status.active {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.integration-status.inactive {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.integration-status span {
  flex: 1;
  text-align: center;
  margin: 0 5px;
}

/* Configuración de BD */
.branch-config {
  text-align: center;
  padding-top: 10px;
  border-top: 1px solid #e0e0e0;
}

.config-indicator {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #17a2b8;
  font-weight: 500;
}

/* Secciones del formulario */
.section-title {
  color: #4a5568;
  font-weight: 500;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
  font-size: 1.1rem;
  letter-spacing: 0.2px;
}

.section-title i {
  margin-right: 12px;
  color: #667eea;
  opacity: 0.8;
}

/* Botones */
.edit-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
}

.btn-round {
  border-radius: 25px;
  padding: 10px 30px;
  font-weight: 500;
}

/* Modal */
.modal-primary .modal-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

/* Badges */
.badge {
  font-size: 0.75rem;
  padding: 4px 8px;
  margin-left: 10px;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .branches-container {
    max-height: 500px;
  }
}

@media (max-width: 992px) {
  .welcome-title {
    font-size: 2rem;
  }
  
  .welcome-subtitle {
    font-size: 1rem;
  }
  
  .welcome-badges {
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .user-profile {
    padding: 10px 0;
  }
  
  .welcome-header {
    padding: 20px;
    text-align: center;
  }
  
  .welcome-title {
    font-size: 1.8rem;
  }
  
  .card-header-primary {
    padding: 30px 15px 60px;
  }
  
  .card-avatar {
    width: 75px;
    height: 75px;
    bottom: -30px;
  }
  
  .card-profile .card-body {
    padding: 55px 15px 15px;
  }
  
  .stat-number {
    font-size: 1.5rem;
  }
  
  .edit-actions {
    flex-direction: column;
    gap: 10px;
  }
  
  .branch-card {
    padding: 15px;
  }
  
  .branch-integrations {
    flex-direction: column;
    gap: 5px;
  }
  
  .access-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }
  
  .access-content {
    width: 100%;
  }
  
  .info-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }
}

@media (max-width: 576px) {
  .welcome-badges .badge {
    font-size: 0.8rem;
    padding: 6px 12px;
  }
  
  .branches-container {
    max-height: 400px;
  }
  
  .branch-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}

/* Scrollbar personalizado */
.branches-container::-webkit-scrollbar {
  width: 6px;
}

.branches-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.branches-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.branches-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
